import { CartLinesUpdateEvent } from '@shopify/events';

class WonderWallCollectionLanding extends HTMLElement {
  connectedCallback() {
    const availabilityParam = this.dataset.availabilityParam;
    const availabilityURL = new URL(location.href);
    if (availabilityParam && !availabilityURL.searchParams.has(availabilityParam)) {
      availabilityURL.searchParams.set(availabilityParam, '1');
      availabilityURL.searchParams.delete('page');
      location.replace(availabilityURL);
      return;
    }
    this.refreshAvailabilityLinks();
    this.hideLegacyCollectionSurfaces();
    this.grid = this.querySelector('[data-ww-grid]');
    this.pagination = this.querySelector('[data-ww-pagination]');
    this.pageSize = Number(this.dataset.pageSize) || 60;
    let productPosition = 0;
    this.spotlights = [...(this.grid?.children || [])].flatMap(item => {
      if (item.matches('[data-ww-product-card]')) productPosition += 1;
      return item.matches('[data-ww-spotlight]') ? [{ position: productPosition, markup: item.outerHTML }] : [];
    });
    this.productEvents();
    this.tabs();
    this.description();
    this.filters();
    this.searchAndSort();
    this.discoveryLinks();
    this.paginationEvents();
    this.moveSoldOutProductsToEnd();
    this.updateRecoveryLinks();
    this.querySelector('[data-ww-query-retry]')?.addEventListener('click', () => {
      if (!this.failedClientQuery) return;
      this.catalogPromise = null;
      this.applyClientQuery(this.failedClientQuery.query, this.failedClientQuery.price);
    });
    this.standardResults = {
      grid: this.grid?.innerHTML || '',
      pagination: this.pagination?.outerHTML || '',
      count: this.querySelector('[data-ww-result-count]')?.textContent || '',
      summary: this.querySelector('[data-ww-page-summary]')?.textContent || '',
    };
    this.updateGlobalSearchLink();

    const params = new URLSearchParams(location.search);
    const query = params.get('ww_q')?.trim() || '';
    const price = params.get('ww_price') || '';
    const searchInput = this.querySelector('[name="ww_q"]');
    if (searchInput) searchInput.value = query;
    this.updateGlobalSearchLink();
    const priceInput = this.querySelector(`[data-ww-price-filter][value="${CSS.escape(price)}"]`);
    if (priceInput) priceInput.checked = true;
    const initialPage = Number(params.get('page')) || 1;
    if (query || price) this.applyClientQuery(query, price, Math.max(1, initialPage), false);
  }

  hideLegacyCollectionSurfaces() {
    document.body.classList.add('ww-has-collection-landing');

    const legacySelectors = [
      '#fdt-grid-container',
      'findter-search-filters',
      'fdt-search-filters',
      '.findter-search-filters',
      '.search-filters',
      'collection-component',
      'results-list.product-grid-container',
      '.section.product-grid-container',
    ];

    const removeLegacy = () => {
      document.querySelectorAll(legacySelectors.join(',')).forEach((element) => {
        if (!element.closest('ww-collection-landing')) element.remove();
      });
    };

    removeLegacy();

    this.legacyObserver?.disconnect();
    this.legacyObserver = new MutationObserver(removeLegacy);
    this.legacyObserver.observe(document.body, { childList: true, subtree: true });
  }

  tabs() {
    this.querySelectorAll('[data-ww-tab]').forEach((tab) => {
      tab.addEventListener('click', () => {
        this.querySelectorAll('[data-ww-tab]').forEach((item) => item.setAttribute('aria-selected', String(item === tab)));
        this.querySelectorAll('[data-ww-panel]').forEach((panel) => panel.classList.toggle('is-hidden', panel.dataset.wwPanel !== tab.dataset.wwTab));
      });
    });
  }

  description() {
    const toggle = this.querySelector('[data-ww-description-toggle]');
    const description = this.querySelector('[data-ww-description]');
    if (!toggle || !description) return;
    toggle.addEventListener('click', () => {
      const expanded = !description.classList.contains('is-expanded');
      description.classList.toggle('is-expanded', expanded);
      toggle.setAttribute('aria-expanded', String(expanded));
      toggle.textContent = expanded ? 'Read less' : 'Read more';
      if (matchMedia('(max-width: 749px)').matches) this.toggleBackdrop(expanded);
    });
  }

  filters() {
    const sheet = this.querySelector('[data-ww-filter-sheet]');
    this.closeSheet(false);
    this.filterDialog?.remove();
    this.filterDialog = null;
    this.filterPlaceholder?.remove();
    this.filterController?.abort();
    this.filterController = new AbortController();
    const options = { signal: this.filterController.signal };
    this.querySelector('[data-ww-filter-open]')?.addEventListener('click', () => {
      if (!sheet || !matchMedia('(max-width:989px)').matches) return;
      if (!this.filterDialog) {
        this.filterPlaceholder = document.createComment('Filter panel desktop position');
        sheet.before(this.filterPlaceholder);
        this.filterDialog = document.createElement('dialog');
        this.filterDialog.className = 'ww-filter-dialog';
        this.filterDialog.setAttribute('aria-labelledby', sheet.querySelector('[data-ww-filter-heading]').id);
        this.append(this.filterDialog);
        this.filterDialog.addEventListener('cancel', event => { event.preventDefault(); this.closeSheet(); }, options);
        this.filterDialog.addEventListener('keydown', event => {
          if (event.key !== 'Tab') return;
          const targets = [...this.filterDialog.querySelectorAll('button, input, select, a[href], summary, [tabindex]')]
            .filter(element => !element.disabled && element.tabIndex >= 0 && element.getClientRects().length);
          const first = targets[0], last = targets.at(-1);
          if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
          else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
        }, options);
      }
      this.filterSnapshot = [...sheet.querySelectorAll('input')].map(input => [input, input.checked]);
      this.filterClearSnapshot = this.explicitFilterClear;
      this.filterReturnFocus = document.activeElement;
      this.filterScroll = window.scrollY;
      this.filterSavedBody = {};
      for (const key of ['position', 'top', 'left', 'right', 'width', 'overflow']) this.filterSavedBody[key] = document.body.style[key];
      Object.assign(document.body.style, { position:'fixed', top:`-${this.filterScroll}px`, left:'0', right:'0', width:'100%', overflow:'hidden' });
      this.filterDialog.append(sheet);
      this.filterDialog.style.setProperty('--ww-filter-height', `${Math.min(760, (window.visualViewport?.height || innerHeight) * .9)}px`);
      this.filterDialog.showModal();
      sheet.querySelector('[data-ww-filter-close]').focus();
    });
    window.addEventListener('resize', () => {
      if (matchMedia('(min-width:990px)').matches) this.closeSheet();
    }, options);
    window.visualViewport?.addEventListener('resize', () => {
      this.filterDialog?.style.setProperty('--ww-filter-height', `${Math.min(760, (window.visualViewport.height || innerHeight) * .9)}px`);
    }, options);
    window.addEventListener('pagehide', () => this.closeSheet(false), options);
    this.querySelector('[data-ww-filter-close]')?.addEventListener('click', () => this.closeSheet());
    this.querySelector('[data-ww-backdrop]')?.addEventListener('click', () => {
      this.closeSheet();
      const description = this.querySelector('[data-ww-description]');
      description?.classList.remove('is-expanded');
      this.querySelector('[data-ww-description-toggle]')?.setAttribute('aria-expanded', 'false');
    });
    this.querySelectorAll('[data-ww-filter-more]').forEach((button) => {
      button.addEventListener('click', () => {
        const options = button.closest('.ww-filter-group__options');
        const expanded = options?.classList.toggle('is-expanded');
        button.textContent = expanded ? 'Show less' : 'Show more';
      });
    });
    this.querySelectorAll('[data-ww-select-all]').forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        const inputs = [...this.querySelectorAll(`[data-ww-tag-filter="${CSS.escape(button.dataset.wwSelectAll)}"], [data-ww-filter-group="${CSS.escape(button.dataset.wwSelectAll)}"]`)];
        const allChecked = inputs.length && inputs.every((input) => input.checked);
        inputs.forEach((input) => { input.checked = !allChecked; });
      });
    });
    this.querySelector('[data-ww-clear-filters]')?.addEventListener('click', () => {
      this.querySelectorAll('[data-ww-tag-filter], [data-ww-native-filter], [data-ww-price-filter]').forEach((input) => { input.checked = false; });
      if (this.dataset.discoveryPilot === 'true') this.explicitFilterClear = true;
    });
    this.querySelector('[data-ww-apply-filters]')?.addEventListener('click', () => this.applyFilters());
    this.querySelectorAll('[data-ww-remove-tag]').forEach((button) => {
      button.addEventListener('click', () => {
        const active = this.activeTagHandles().filter((tag) => tag !== button.dataset.wwRemoveTag);
        this.navigateToTags(active);
      });
    });
  }

  closeSheet(restoreFocus = true) {
    const wasOpen = this.filterDialog?.open;
    if (wasOpen) {
      this.filterSnapshot?.forEach(([input, checked]) => { input.checked = checked; });
      this.explicitFilterClear = this.filterClearSnapshot;
      this.filterDialog.close();
      Object.assign(document.body.style, this.filterSavedBody);
      window.scrollTo(0, this.filterScroll);
    }
    const sheet = this.querySelector('[data-ww-filter-sheet]');
    if (sheet && this.filterPlaceholder?.isConnected) this.filterPlaceholder.after(sheet);
    sheet?.classList.remove('is-open');
    this.toggleBackdrop(false);
    if (wasOpen && restoreFocus) {
      const target = this.filterReturnFocus?.isConnected && this.filterReturnFocus.getClientRects().length ? this.filterReturnFocus : sheet?.querySelector('[data-ww-filter-heading]');
      target?.focus({ preventScroll:true });
    }
  }

  disconnectedCallback() {
    this.closeSheet(false);
    this.filterController?.abort();
    this.legacyObserver?.disconnect();
  }

  toggleBackdrop(show) {
    const backdrop = this.querySelector('[data-ww-backdrop]');
    if (backdrop) backdrop.hidden = !show;
  }

  activeTagHandles() {
    const inputs = [...this.querySelectorAll('[data-ww-tag-filter]')];
    const checked = inputs.filter(input => input.checked).map(input => input.value);
    if (this.dataset.discoveryPilot !== 'true' || this.explicitFilterClear) return checked;
    const represented = new Set(inputs.map(input => input.value));
    const retained = [...this.querySelectorAll('[data-ww-remove-tag]')].map(button => button.dataset.wwRemoveTag).filter(tag => !represented.has(tag));
    return [...new Set([...checked, ...retained])];
  }

  applyFilters() {
    const price = this.querySelector('[data-ww-price-filter]:checked')?.value || '';
    const url = this.explicitFilterClear ? this.clearFiltersURL() : new URL(location.href);
    if (price) url.searchParams.set('ww_price', price); else url.searchParams.delete('ww_price');
    url.searchParams.delete('page');
    const nativeInputs = [...this.querySelectorAll('[data-ww-native-filter]')];
    const nativeParams = new Set(['filter.p.vendor', 'filter.p.product_type', ...nativeInputs.map(input => input.dataset.wwNativeFilter)]);
    nativeParams.forEach(name => url.searchParams.delete(name));
    nativeInputs.filter(input => input.checked).forEach(input => url.searchParams.append(input.dataset.wwNativeFilter, input.value));
    this.navigateToTags(this.activeTagHandles(), url.searchParams);
  }

  navigateToTags(tags, params = new URLSearchParams(location.search)) {
    const path = `/collections/${this.dataset.collectionHandle}${tags.length ? `/${tags.join('+')}` : ''}`;
    params.delete('page');
    location.assign(`${path}${params.toString() ? `?${params}` : ''}`);
  }

  searchAndSort() {
    this.querySelector('[data-ww-sort]')?.addEventListener('change', (event) => {
      const url = new URL(location.href);
      url.searchParams.set('sort_by', event.target.value);
      url.searchParams.delete('page');
      location.assign(url);
    });
    this.querySelector('[name="ww_q"]')?.addEventListener('input', () => this.updateGlobalSearchLink());
    this.querySelector('[data-ww-search-form]')?.addEventListener('submit', (event) => {
      event.preventDefault();
      const query = new FormData(event.currentTarget).get('ww_q')?.toString().trim() || '';
      const url = new URL(location.href);
      if (query) url.searchParams.set('ww_q', query); else url.searchParams.delete('ww_q');
      url.searchParams.delete('page');
      const price = url.searchParams.get('ww_price') || '';
      if (this.dataset.collectionHandle === 'ww-shop-all' && !query && !price) {
        location.assign(url);
        return;
      }
      if (this.clientQueryBudgetExceeded()) {
        this.queryRequest = (this.queryRequest || 0) + 1;
        this.showStandardResults(this.dataset.queryTooLarge);
        return;
      }
      history.replaceState({ ...history.state }, '', url);
      this.discoveryLinks();
      this.applyClientQuery(query, price);
    });
  }

  paginationEvents() {
    this.addEventListener('click', async (event) => {
      const showMore = event.target.closest('[data-ww-show-more]');
      if (showMore) {
        if (this.clientMatches) {
          this.renderClientPage(this.clientPage + 1, true);
        } else {
          await this.loadNextPage(showMore);
        }
        return;
      }
      const clientPage = event.target.closest('[data-ww-client-page]');
      if (clientPage) this.renderClientPage(Number(clientPage.dataset.wwClientPage), false);
    });
  }

  async loadNextPage(button) {
    if (button.disabled) return;
    button.disabled = true;
    button.textContent = 'Loading…';
    const request = this.queryRequest || 0;
    try {
      const url = new URL(button.dataset.nextUrl, location.origin);
      url.searchParams.set('section_id', this.dataset.sectionId);
      const html = await fetch(url).then((response) => {
        if (!response.ok) throw new Error('Unable to load more products.');
        return response.text();
      });
      if (request !== (this.queryRequest || 0)) return;
      const documentFragment = new DOMParser().parseFromString(html, 'text/html');
      documentFragment.querySelectorAll('[data-ww-grid] > *').forEach((item) => this.grid.append(item));
      this.moveSoldOutProductsToEnd();
      const nextPagination = documentFragment.querySelector('[data-ww-pagination]');
      if (nextPagination && this.pagination) this.pagination.replaceWith(nextPagination);
      else this.pagination?.remove();
      this.pagination = this.querySelector('[data-ww-pagination]');
    } catch (error) {
      if (request === (this.queryRequest || 0)) {
        button.disabled = false;
        button.textContent = 'Try again';
      }
    } finally {
      if (request !== (this.queryRequest || 0) && button.isConnected) {
        button.disabled = false;
        button.textContent = 'Show more products';
      }
    }
  }

  clientQueryBudgetExceeded() {
    if (this.dataset.collectionHandle !== 'ww-shop-all' && this.dataset.categoryFirst !== 'true' && !this.isFullBrandScope()) return false;
    const pages = Number(this.grid?.dataset.totalPages);
    const products = Number(this.grid?.dataset.totalProducts);
    return !/^\d+$/.test(this.grid?.dataset.totalPages || '') || !/^\d+$/.test(this.grid?.dataset.totalProducts || '') || !Number.isInteger(pages) || !Number.isInteger(products) || pages < 0 || products < 0 ||
      (products > 0 && pages < 1) || pages > 10 || products > 600;
  }

  updateGlobalSearchLink() {
    this.refreshAvailabilityLinks();
    const input = this.querySelector('[name="ww_q"]');
    const query = input ? input.value.trim() : new URL(location.href).searchParams.get('ww_q') || '';
    this.querySelectorAll('[data-ww-native-brand-search]').forEach(link => {
      const current = new URL(location.href);
      const url = new URL(this.dataset.searchUrl, location.origin);
      url.searchParams.set('type', 'product');
      url.searchParams.set('filter.p.vendor', link.dataset.wwNativeBrandSearch);
      url.searchParams.set('view', 'wonderwall-brand-search');
      if (query) url.searchParams.set('q', this.nativeLiteralQuery(query));
      const availability = current.searchParams.getAll('filter.v.availability');
      if (!availability.length && this.grid?.dataset.availableOnlyFallback === 'true') availability.push('1');
      availability.forEach(value => url.searchParams.append('filter.v.availability', value));
      for (const key of ['filter.v.price.gte', 'filter.v.price.lte']) {
        current.searchParams.getAll(key).forEach(value => url.searchParams.append(key, value));
      }
      link.href = url.href;
      link.hidden = !query;
    });
    const link = this.querySelector('[data-ww-global-search]');
    const clear = this.querySelector('[data-ww-clear-client-query]');
    if (clear) {
      const browseURL = new URL(location.href);
      ['ww_q', 'ww_price', 'page'].forEach(key => browseURL.searchParams.delete(key));
      clear.href = browseURL.href;
    }
    if (!link) return;
    const url = new URL(this.dataset.searchUrl, location.origin);
    url.searchParams.set('type', 'product');
    if (query) url.searchParams.set('q', query);
    link.href = url.href;
  }

  isFullBrandScope() {
    return this.dataset.fullBrand === 'true' || ['cosrx', 'apotheke'].includes(this.dataset.collectionHandle) ||
      (this.dataset.collectionHandle === 'vendors' && Boolean(new URL(location.href).searchParams.get('q')));
  }

  nativeLiteralQuery(query) {
    return `"${query.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`;
  }

  refreshAvailabilityLinks() {
    if (!this.dataset.availabilityParam) return;
    this.querySelectorAll('[data-ww-availability]').forEach(link => {
      const url = new URL(location.href);
      url.searchParams.set(this.dataset.availabilityParam, link.dataset.wwAvailability);
      url.searchParams.delete('page');
      link.href = url.href;
    });
  }

  clearFiltersURL() {
    const url = new URL(location.href);
    url.pathname = `/collections/${this.dataset.collectionHandle}`;
    for (const key of [...url.searchParams.keys()]) if (key.startsWith('filter.')) url.searchParams.delete(key);
    url.searchParams.delete('ww_price');
    url.searchParams.delete('page');
    if (this.dataset.availabilityParam) {
      url.searchParams.append(this.dataset.availabilityParam, '1');
      url.searchParams.append(this.dataset.availabilityParam, '0');
    }
    return url;
  }

  showStandardResults(message) {
    if (this.clientMatches) {
      this.grid.innerHTML = this.standardResults.grid;
      if (this.pagination?.isConnected) this.pagination.remove();
      if (this.standardResults.pagination) this.querySelector('.ww-results')?.insertAdjacentHTML('beforeend', this.standardResults.pagination);
      this.pagination = this.querySelector('[data-ww-pagination]');
    }
    this.clientMatches = null;
    this.grid.removeAttribute('aria-busy');
    if (this.pagination) this.pagination.hidden = false;
    const count = this.querySelector('[data-ww-result-count]');
    if (count) count.textContent = this.standardResults.count;
    const summary = this.querySelector('[data-ww-page-summary]');
    if (summary) summary.textContent = this.standardResults.summary;
    const notice = this.querySelector('[data-ww-query-notice]');
    if (notice) { notice.textContent = message; notice.hidden = false; }
    else if (count) count.textContent = message;
    this.updateGlobalSearchLink();
  }

  async loadCompleteCollection() {
    if (this.clientQueryBudgetExceeded()) throw new Error('Selection exceeds local search performance budget.');
    if (this.catalogPromise) return this.catalogPromise;
    this.catalogPromise = (async () => {
      const totalPages = Number(this.grid.dataset.totalPages) || 1;
      const expected = Number(this.grid.dataset.totalProducts);
      if (expected > 25000) throw new Error('Collection exceeds complete pagination limit.');
      if (expected === 0) return [];
      const base = new URL(location.href);
      ['page', 'section_id', 'ww_q', 'ww_price'].forEach(key => base.searchParams.delete(key));
      const pages = new Array(totalPages);
      let nextPage = 1;
      const worker = async () => {
        while (nextPage <= totalPages) {
          const page = nextPage++;
          const url = new URL(base);
          url.searchParams.set('page', String(page));
          url.searchParams.set('section_id', this.dataset.sectionId);
          const response = await fetch(url);
          if (!response.ok) throw new Error('Unable to load complete collection.');
          const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
          const grid = parsed.querySelector('[data-ww-grid]');
          if (!grid || Number(grid.dataset.currentPage) !== page || Number(grid.dataset.totalPages) !== Number(this.grid.dataset.totalPages) || Number(grid.dataset.totalProducts) !== expected) throw new Error('Collection changed while loading.');
          pages[page - 1] = [...grid.querySelectorAll('[data-ww-product-card]')].map(card => ({
            id: card.dataset.productId, markup: card.outerHTML, available: card.dataset.available === 'true',
            search: card.dataset.search || '', price: Number(card.dataset.price),
          }));
        }
      };
      await Promise.all(Array.from({ length: Math.min(3, totalPages) }, worker));
      const found = new Map(pages.flat().map(card => [card.id, card]));
      const availableOnlyFallback = this.grid.dataset.availableOnlyFallback === 'true';
      if (!Number.isFinite(expected) || (!availableOnlyFallback && found.size !== expected)) throw new Error('Incomplete collection results.');
      return [...found.values()].sort((first, second) => Number(second.available) - Number(first.available));
    })().catch(error => { this.catalogPromise = null; throw error; });
    return this.catalogPromise;
  }

  async applyClientQuery(query, priceRange, page = 1, scroll = true) {
    if (!this.grid) return;
    const request = this.queryRequest = (this.queryRequest || 0) + 1;
    this.failedClientQuery = null;
    const retry = this.querySelector('[data-ww-query-retry]');
    if (retry) retry.hidden = true;
    if (this.clientQueryBudgetExceeded()) {
      this.showStandardResults(this.dataset.queryTooLarge);
      return;
    }
    const notice = this.querySelector('[data-ww-query-notice]');
    if (notice) notice.hidden = true;
    if (this.dataset.discoveryPilot === 'true') {
      const rail = this.querySelector('[data-ww-discovery-rail]');
      if (rail) rail.hidden = Boolean(query || priceRange);
      this.querySelectorAll('[data-ww-discovery-editorial]').forEach(element => { element.hidden = Boolean(query || priceRange); });
    }
    const count = this.querySelector('[data-ww-result-count]');
    if (count) count.textContent = 'Loading products…';
    this.grid.setAttribute('aria-busy', 'true');
    if (this.pagination) this.pagination.hidden = true;
    try {
      const cards = await this.loadCompleteCollection();
      if (request !== this.queryRequest) return;
      const normalized = query.toLowerCase();
      const [minimum, maximum] = priceRange ? priceRange.split('-').map(Number) : [0, 0];
      this.clientMatches = cards.filter(card => (!normalized || card.search.includes(normalized)) &&
        (!priceRange || (card.price >= minimum && (maximum === 0 || card.price < maximum)))).map(card => card.markup);
      this.renderClientPage(page, false, scroll);
    } catch (error) {
      if (request !== this.queryRequest) return;
      this.showStandardResults(this.dataset.queryUnavailable || 'Complete ordering unavailable. Showing standard results; reload to retry.');
      this.failedClientQuery = { query, price:priceRange };
      if (retry) retry.hidden = false;
    } finally {
      if (request === this.queryRequest) this.grid.removeAttribute('aria-busy');
    }
  }

  markupIsAvailable(markup) {
    const holder = document.createElement('div');
    holder.innerHTML = markup;
    return holder.firstElementChild?.dataset.available === 'true';
  }

  moveSoldOutProductsToEnd() {
    if (!this.grid) return;
    const cards = [...this.grid.querySelectorAll(':scope > [data-ww-product-card]')];
    if (cards.length < 2) return;
    const orderedCards = cards
      .map((card, index) => ({ card, index, available: card.dataset.available === 'true' }))
      .sort((first, second) => Number(second.available) - Number(first.available) || first.index - second.index)
      .map(({ card }) => card);
    const slots = cards.map(() => document.createComment('WonderWall product slot'));
    cards.forEach((card, index) => card.replaceWith(slots[index]));
    slots.forEach((slot, index) => slot.replaceWith(orderedCards[index]));
  }

  renderClientPage(page, append, scroll = true) {
    if (!this.clientMatches) return;
    const totalPages = Math.max(1, Math.ceil(this.clientMatches.length / this.pageSize));
    this.clientPage = Math.min(Math.max(page, 1), totalPages);
    const start = (this.clientPage - 1) * this.pageSize;
    const end = start + this.pageSize;
    const selected = this.clientMatches.slice(start, end);
    const markup = selected.map((card, index) => this.spotlights.filter(item => item.position === index).map(item => item.markup).join('') + card).join('');
    if (append) this.grid.insertAdjacentHTML('beforeend', markup); else this.grid.innerHTML = markup || this.emptySearchMarkup();
    this.moveSoldOutProductsToEnd();
    const count = this.querySelector('[data-ww-result-count]');
    if (count) count.textContent = `${this.clientMatches.length} ${this.clientMatches.length === 1 ? 'result' : 'results'}`;
    const pages = Array.from({ length: totalPages }, (_, index) => {
      const value = index + 1;
      return value === this.clientPage ? `<span aria-current="page">${value}</span>` : `<button type="button" data-ww-client-page="${value}">${value}</button>`;
    }).join('');
    const pagination = `<nav class="ww-pagination" aria-label="Filtered result pages" data-ww-pagination>${this.clientPage < totalPages ? '<button type="button" class="ww-button" data-ww-show-more>Show more products</button>' : ''}<div class="ww-pagination__pages">${pages}</div></nav>`;
    if (this.pagination?.isConnected) this.pagination.outerHTML = pagination;
    else this.querySelector('.ww-results')?.insertAdjacentHTML('beforeend', pagination);
    this.pagination = this.querySelector('[data-ww-pagination]');
    const summary = this.querySelector('[data-ww-page-summary]');
    if (summary) summary.textContent = `Page ${this.clientPage} of ${totalPages}`;
    const url = new URL(location.href);
    url.searchParams.set('page', String(this.clientPage));
    history.replaceState({ ...history.state }, '', url);
    this.discoveryLinks();
    if (!append && scroll) this.querySelector('.ww-results')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  discoveryTagURL(tag, remove = false) {
    const url = new URL(location.href);
    const current = this.activeTagHandles();
    const tags = remove ? current.filter(value => value !== tag) : [...new Set([...current, tag])];
    url.pathname = `/collections/${this.dataset.collectionHandle}${tags.length ? `/${tags.join('+')}` : ''}`;
    url.searchParams.delete('page');
    return url;
  }

  discoveryLinks() {
    this.refreshAvailabilityLinks();
    if (this.dataset.discoveryPilot !== 'true') return;
    this.querySelectorAll('[data-ww-discovery-tag]').forEach(link => {
      link.href = this.discoveryTagURL(link.dataset.wwDiscoveryTag, link.dataset.wwDiscoveryAction === 'remove').href;
    });
    this.querySelectorAll('[data-ww-discovery-native]').forEach(link => {
      link.href = this.discoveryNativeURL(link.href, link.dataset.wwDiscoveryNative).href;
    });
    this.querySelectorAll('[data-ww-preserve-query]').forEach(link => {
      const url = new URL(link.getAttribute('href'), location.origin);
      const query = new URL(location.href).searchParams.get('ww_q');
      if (query) url.searchParams.set('ww_q', query); else url.searchParams.delete('ww_q');
      link.href = url.href;
    });
    this.querySelectorAll('[data-ww-remove-native]').forEach(link => {
      const url = new URL(location.href), key = link.dataset.wwRemoveNative;
      const values = url.searchParams.getAll(key).filter(value => value !== link.dataset.wwRemoveValue);
      url.searchParams.delete(key);
      values.forEach(value => url.searchParams.append(key, value));
      if (key === this.dataset.availabilityParam && !values.length) {
        url.searchParams.append(key, '1'); url.searchParams.append(key, '0');
      }
      url.searchParams.delete('page'); link.href = url.href;
    });
    this.updateRecoveryLinks();
  }

  updateRecoveryLinks(container = this) {
    const current = new URL(location.href);
    const isBaseBrand = this.dataset.emptyBrand && current.pathname.split('/').length === 3 && !current.searchParams.get('ww_q') && !current.searchParams.get('ww_price') && ![...current.searchParams.keys()].some(key => key.startsWith('filter.') && !['filter.p.vendor', 'filter.v.availability'].includes(key));
    if (isBaseBrand && this.grid?.dataset.totalProducts === '0' && /^\d+$/.test(this.dataset.brandOrderableCount || '') && /^\d+$/.test(this.dataset.brandUnavailableCount || '')) {
      const message = this.grid.querySelector('.ww-results__empty p');
      if (message) message.textContent = Number(this.dataset.brandUnavailableCount) > 0 ? this.dataset.noBrandOrderable : this.dataset.noBrandPublished;
    }
    const clearFilters = this.clearFiltersURL();
    const hasRefinements = current.pathname.split('/').length > 3 || current.searchParams.has('ww_price') || [...current.searchParams.keys()].some(key => key.startsWith('filter.'));
    container.querySelectorAll('[data-ww-clear-refinements]').forEach(link => { link.href = clearFilters.href; link.hidden = !hasRefinements && Boolean(current.searchParams.get('ww_q')); });
    container.querySelectorAll('[data-ww-clear-search-only]').forEach(link => {
      link.href = this.clearSearchURL().href;
      link.hidden = !current.searchParams.get('ww_q');
    });
    container.querySelectorAll('[data-ww-clear-price-only]').forEach(link => {
      const url = new URL(current); url.searchParams.delete('ww_price'); url.searchParams.delete('page');
      link.href = url.href; link.hidden = !current.searchParams.get('ww_price');
    });
    this.querySelector('.ww-client-filters')?.classList.toggle('is-hidden', !current.searchParams.get('ww_q') && !current.searchParams.get('ww_price'));
    container.querySelectorAll('[data-ww-view-unavailable]').forEach(link => {
      const url = new URL(current);
      url.searchParams.delete('filter.v.availability');
      url.searchParams.append('filter.v.availability', '1');
      url.searchParams.append('filter.v.availability', '0');
      url.searchParams.delete('page');
      link.href = url.href;
      if (isBaseBrand) link.textContent = this.dataset.viewAllBrand;
      link.hidden = current.searchParams.getAll('filter.v.availability').join(',') !== '1' || (isBaseBrand && Number(this.dataset.brandUnavailableCount) === 0);
    });
  }

  discoveryNativeURL(href, param) {
    const values = new URL(href, location.href).searchParams.getAll(param);
    const url = new URL(location.href);
    url.searchParams.delete(param);
    values.forEach(value => url.searchParams.append(param, value));
    url.searchParams.delete('page');
    return url;
  }

  clearSearchURL() {
    const url = new URL(location.href);
    url.searchParams.delete('ww_q');
    url.searchParams.delete('page');
    return url;
  }

  emptySearchMarkup() {
    if (this.dataset.discoveryPilot !== 'true') return '<p class="ww-results__empty">No products match your search.</p>';
    const message = document.createElement('p');
    message.className = 'ww-results__empty';
    message.textContent = new URL(location.href).searchParams.get('ww_q') ? this.dataset.noSearchResults : (this.dataset.noFilterResults || this.dataset.noSearchResults);
    const template = this.querySelector('[data-ww-empty-template]');
    if (template) {
      const recovery = document.createElement('div');
      recovery.innerHTML = template.innerHTML;
      this.updateRecoveryLinks(recovery);
      return message.outerHTML + recovery.innerHTML;
    }
    if (new URL(location.href).searchParams.get('ww_q')) {
      const link = document.createElement('a');
      link.href = this.clearSearchURL().href;
      link.textContent = this.dataset.clearSearchLabel;
      message.append(' ', link);
    }
    return message.outerHTML;
  }

  productEvents() {
    this.addEventListener('click', (event) => {
      const more = event.target.closest('[data-ww-variants-more]');
      if (more) {
        const wrapper = more.closest('.ww-card__variants');
        const expanded = wrapper?.classList.toggle('is-expanded');
        more.setAttribute('aria-expanded', String(expanded));
        more.textContent = expanded ? '−' : '+';
        return;
      }
      const variant = event.target.closest('[data-ww-variant]');
      if (variant) this.selectVariant(variant);
      const add = event.target.closest('[data-ww-add]');
      if (add) this.changeQuantity(add.closest('[data-ww-purchase]'), 1, true);
      const plus = event.target.closest('[data-ww-plus]');
      if (plus) this.changeQuantity(plus.closest('[data-ww-purchase]'), 1, false);
      const minus = event.target.closest('[data-ww-minus]');
      if (minus) this.changeQuantity(minus.closest('[data-ww-purchase]'), -1, false);
    });
  }

  selectVariant(button) {
    if (button.disabled || button.dataset.available !== 'true') return;
    const card = button.closest('[data-ww-product-card]');
    card.querySelectorAll('[data-ww-variant]').forEach((item) => item.classList.toggle('is-selected', item === button));
    card.querySelector('[data-ww-purchase]').dataset.variantId = button.dataset.variantId;
    card.querySelector('[data-ww-price]').textContent = button.dataset.price;
    const compare = card.querySelector('[data-ww-compare]');
    compare.textContent = button.dataset.comparePrice || '';
    compare.classList.toggle('is-hidden', !button.dataset.comparePrice);
    if (button.dataset.image) card.querySelector('.ww-card__image').src = button.dataset.image;
    const add = card.querySelector('[data-ww-add]');
    add.disabled = button.dataset.available !== 'true';
    add.textContent = add.disabled ? 'Sold out' : 'Add to basket';
    card.querySelector('[data-ww-stepper]').hidden = true;
    add.hidden = false;
    card.querySelector('[data-ww-purchase]').dataset.quantity = '0';
    card.querySelector('[data-ww-status]').textContent = '';
  }

  async changeQuantity(purchase, delta, isAdd) {
    if (!purchase || purchase.dataset.busy === 'true') return;
    const variantId = purchase.dataset.variantId;
    if (!variantId) {
      purchase.querySelector('[data-ww-status]').textContent = this.dataset.sizeHint || 'Choose a size before adding to bag.';
      return;
    }
    if (delta > 0 && purchase.querySelector('[data-ww-add]').disabled) return;
    const current = Number(purchase.dataset.quantity) || 0;
    const target = Math.max(0, current + delta);
    purchase.dataset.busy = 'true';
    const status = purchase.querySelector('[data-ww-status]');
    status.textContent = '';
    const deferred = CartLinesUpdateEvent.createPromise();
    purchase.dispatchEvent(new CartLinesUpdateEvent({
      action: isAdd ? 'add' : 'update',
      context: 'collection',
      lines: [{ merchandiseId: variantId, quantity: target }],
      promise: deferred.promise,
    }));
    try {
      const sectionIds = [...document.querySelectorAll('cart-items-component[data-section-id]')].map((item) => item.dataset.sectionId).filter(Boolean);
      let response;
      if (isAdd || current === 0) {
        response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }], sections: sectionIds, sections_url: location.pathname }),
        }).then((result) => result.json());
      } else {
        const cartBefore = await fetch(`${window.Shopify.routes.root}cart.js`).then((result) => result.json());
        const line = cartBefore.items.find((item) => String(item.variant_id) === String(variantId));
        response = await fetch(`${window.Shopify.routes.root}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: line?.key || variantId, quantity: target, sections: sectionIds, sections_url: location.pathname }),
        }).then((result) => result.json());
      }
      if (response.status) throw new Error(response.description || response.message || 'Unable to update cart.');
      const cart = await fetch(`${window.Shopify.routes.root}cart.js`).then((result) => result.json());
      purchase.dataset.quantity = String(target);
      purchase.querySelector('[data-ww-quantity]').textContent = String(target);
      purchase.querySelector('[data-ww-add]').hidden = target > 0;
      purchase.querySelector('[data-ww-stepper]').hidden = target === 0;
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { didError: false, itemCount: target, sections: response.sections, source: 'wonderwall-collection' },
      });
    } catch (error) {
      status.textContent = error.message;
      deferred.reject(error);
    } finally {
      purchase.dataset.busy = 'false';
    }
  }
}

if (!customElements.get('ww-collection-landing')) customElements.define('ww-collection-landing', WonderWallCollectionLanding);
